import React from "react";
import { Package, Edit, Trash2, Check, Clock, Link } from "lucide-react";
import { Plan } from "../dataTypes";
import { notifySuccess } from "Common/notify";

interface Props {
  plan: Plan;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

const PlanCard: React.FC<Props> = ({ plan, onEdit, onDelete }) => {
  const monthly = plan.prices.find((p) => p.billingInterval === "month");
  const yearly = plan.prices.find((p) => p.billingInterval === "year");
  const isActive = plan.prices.some((p) => p.active);

  const copyToClipboard = (url: string) => {
    navigator.clipboard.writeText(url).then(() => {
      notifySuccess("Link copied!");
    });
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-5 hover:shadow-md transition">
      {/* Header */}
      <div className="flex justify-between items-start mb-4">
        <div>
          <h2 className="text-xl font-bold flex items-center text-gray-900 dark:text-white">
            <Package className="h-5 w-5 mr-2 text-indigo-500" />
            {plan.title}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">{plan.description}</p>
        </div>
        <div className="flex items-center gap-3">
          <span
            className={`px-3 py-1 rounded-full text-xs font-medium flex items-center ${
              isActive
                ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
            }`}
          >
            <Check className="h-4 w-4 mr-1" />
            {isActive ? "Live" : "Draft"}
          </span>
          <button onClick={() => onEdit(plan?._id)} className="p-2 text-gray-500 hover:text-indigo-500">
            <Edit className="h-5 w-5" />
          </button>
          <button onClick={() => onDelete(plan?._id)} className="p-2 text-gray-500 hover:text-red-500">
            <Trash2 className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Grid Info */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
        <div>
          <h3 className="text-xs uppercase text-gray-500 dark:text-gray-400 font-semibold mb-1">
            Pricing
          </h3>
          {monthly && (
            <p className="flex items-center gap-2">
              Monthly: ${monthly.amount}{" "}
              {monthly?.paymentLinkUrl && (
                <button
                  onClick={() => copyToClipboard(monthly?.paymentLinkUrl)}
                  className="text-indigo-500 hover:text-indigo-700 flex items-center"
                  title="Copy selling link"
                >
                  <Link className="h-4 w-4" />
                </button>
              )}
            </p>
          )}
          {yearly && (
            <p className="flex items-center gap-2">
              Yearly: ${yearly.amount}{" "}
              {yearly?.paymentLinkUrl && (
                <button
                  onClick={() => copyToClipboard(yearly?.paymentLinkUrl)}
                  className="text-indigo-500 hover:text-indigo-700 flex items-center"
                  title="Copy selling link"
                >
                  <Link className="h-4 w-4" />
                </button>
              )}
            </p>
          )}
        </div>

        <div>
          <h3 className="text-xs uppercase text-gray-500 dark:text-gray-400 font-semibold mb-1">
            Limits
          </h3>
          <p>Users: {plan.userLimit || "Unlimited"}</p>
          <p>Contacts: {plan.contactLimit || "Unlimited"}</p>
        </div>

        <div>
          <h3 className="text-xs uppercase text-gray-500 dark:text-gray-400 font-semibold mb-1">
            Trial & Credits
          </h3>
          <p className="flex items-center">
            <Clock className="h-4 w-4 mr-1 text-gray-400" />
            {plan.trialPeriod.active ? `${plan.trialPeriod.days} days` : "No Trial"}
          </p>
          <p>
            Credits:{" "}
            {plan.credits.active ? `${plan.credits.amount} (${plan.credits.creditType})` : "None"}
          </p>
        </div>

        <div className="sm:col-span-2 lg:col-span-3">
          <h3 className="text-xs uppercase text-gray-500 dark:text-gray-400 font-semibold mb-1">
            SaaS Products
          </h3>
          <div className="flex flex-wrap gap-2">
            {plan.saasProducts.map((p, i) => (
              <span
                key={i}
                className="px-3 py-1 text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300 rounded-full"
              >
                {p}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanCard;
